import { BooleanString, CreateTicketForm, EventCanGoLive, TicketBody } from './types';
import { convertDateWithTimezoneToUTC, convertUTCToDate, keepUpperCase } from 'utils/utils';
import { date, number, object, string } from 'yup';

import { EventData } from 'features/common/types';
import { TFunction } from 'react-i18next';
import { camelizeKeys } from 'humps';
import { isNewImg } from 'features/EventImgContent/utils';
import type { UploadImageProps } from 'services/UploadImage/types';

export const NEW_TICKET = 'new-ticket';

const yesterday = new Date();
yesterday.setDate(yesterday.getDate() - 1);
yesterday.setHours(0, 0, 0, 0);
yesterday.setMinutes(0);

export const defaultValues: CreateTicketForm = {
	amount: undefined,
	buyerAbsorbPaymentFee: 'false',
	buyerAbsorbTixologistFee: 'false',
	initialPrice: null,
	name: undefined,
	// 0 = "No pool" — the explicit default option in the pool dropdown (not undefined, so the
	// "No pool" option renders as selected by default).
	poolId: 0,
	bundleSize: undefined,
	secondaryRoyalty: undefined,
	sellingTime: yesterday,
	offSellingTime: null,
	description: '',
	allowSecondarySales: false,
	template: undefined,
	ticketImage: undefined,
	ticketImageStaged: [],
	ticketVisibility: true,
	image: '',
	displayType: '',
	privateLink: false,
	hiddenCode: null,
	showImage: false,
	seatsCategoryKey: undefined,
	presaleCode: '',
	alwaysShowTickets: false,
	payWhatYouWant: false,
	minPrice: null,
	maxPrice: null,
	maxQuantity: null,
	percentageFee: null,
	fixedFee: null,
	color: null,
};

export const validationSchema = (t: TFunction<'translation', undefined>, getAmountFloor: () => number = () => 0) =>
	object().shape({
		name: string().required(t('ticketDetails-requiredTicketTypeName')),
		// Required only for fixed-price tiers. In pay-what-you-want mode the price comes from the
		// min/max range and the initialPrice field is hidden, so an unconditional `.required()` would
		// block save with an error that has nowhere to render — which is why a destructive reset-to-0
		// effect used to paper over it. Conditional-required removes the need for that effect (RX-8).
		initialPrice: number()
			.typeError(t('ticketDetails-requiredTicketPrice'))
			.nullable()
			.when('payWhatYouWant', {
				is: true,
				then: (s) => s,
				otherwise: (s) => s.required(t('ticketDetails-requiredTicketPrice')),
			}),
		amount: number()
			.typeError(t('ticketDetails-requiredQuantity'))
			.required()
			// Quantity can never drop below what has already sold (docs/pools_business_logic.md, T-4).
			// The floor is read at validation time so it reflects the loaded ticket, not the initial render.
			.test('above-sold', '', function (val) {
				const floor = getAmountFloor();
				if ((val ?? 0) >= floor) return true;
				return this.createError({
					message: t('ticketDetails-quantity-min-sold', "Can't be lower than the {{count}} tickets already sold.", {
						count: floor,
					}),
				});
			}),
		sellingTime: date().typeError(t('ticketDetails-requiredActiveTime')).required(),
		poolId: string().typeError(t('ticketDetails-requiredPool')).optional(),
		// UX guards (the backend enforces none of these — see docs/...; the numeric clamp gives live
		// feedback, these yup rules are the submit-time authority). nullable() so an empty optional field
		// passes; the rule only bites on an actual out-of-range number.
		percentageFee: number()
			.nullable()
			.min(0, t('ticketDetails-percentageFee-range', 'Enter a percentage between 0 and 100.'))
			.max(100, t('ticketDetails-percentageFee-range', 'Enter a percentage between 0 and 100.')),
		fixedFee: number().nullable().min(0, t('ticketDetails-fixedFee-min', "A fee can't be negative.")),
		maxQuantity: number().nullable().min(1, t('ticketDetails-maxQuantity-min', 'Must be at least 1.')),
		bundleSize: number().nullable().min(1, t('ticketDetails-bundleSize-min', 'Must be at least 1.')),
		// Pay-what-you-want range: only enforced when PWYW is on; the max must be at least the min, and
		// neither is negative. An inverted range saves on the backend but makes the tier unbuyable, so we
		// guard it on the form (mirrors the purchase-time runtime check).
		minPrice: number()
			.nullable()
			.when('payWhatYouWant', {
				is: true,
				then: (s) => s.min(0, t('ticketDetails-minPrice-min', "Can't be negative.")),
				otherwise: (s) => s,
			}),
		maxPrice: number()
			.nullable()
			.when('payWhatYouWant', {
				is: true,
				then: (s) =>
					s
						.min(0, t('ticketDetails-maxPrice-min', "Can't be negative."))
						.test(
							'max-gte-min',
							t('ticketDetails-maxPrice-below-min', 'The maximum must be at least the minimum.'),
							function (val) {
								const minPrice = this.parent?.minPrice;
								if (val == null || minPrice == null) return true;
								return val >= minPrice;
							}
						),
				otherwise: (s) => s,
			}),
	});

export const createBody = (
	ticketForm: CreateTicketForm,
	eventId: number,
	timezone: string,
	ticketId?: number
): TicketBody => {
	const _sellingTime = new Date(ticketForm.sellingTime ?? 0);
	const _offSellingTime = ticketForm.offSellingTime
		? convertDateWithTimezoneToUTC(new Date(ticketForm.offSellingTime), timezone)
		: null;
	const poolId =
		!isNaN(Number(ticketForm.poolId)) && Number(ticketForm.poolId) > 0 ? Number(ticketForm.poolId) : undefined;
	// Persist only the active pricing mode's fields and zero the dormant ones. This reproduces exactly
	// what the form's former reset effect used to persist, but here — at serialization time — so the
	// in-form values survive a round-trip toggle instead of being clobbered to 0 (RX-8).
	const pwyw = ticketForm.payWhatYouWant;
	const _ticketForm = {
		allowSecondarySales: ticketForm.allowSecondarySales,
		amount: Number(ticketForm.amount),
		buyerAbsorbPaymentFee: ticketForm.buyerAbsorbPaymentFee === 'true',
		buyerAbsorbTixologistFee: ticketForm.buyerAbsorbTixologistFee === 'true',
		description: ticketForm.description,
		ticketVisibility: ticketForm.ticketVisibility,
		privateLink: ticketForm.privateLink,
		hidden_code: ticketForm.hiddenCode,
		showImage: ticketForm.showImage,
		seatsCategoryKey: ticketForm.seatsCategoryKey ? String(ticketForm.seatsCategoryKey) : undefined,
		presaleCode: ticketForm.presaleCode,
		alwaysShowTickets: ticketForm.alwaysShowTickets,
		eventId,
		//PUT
		ID: ticketId ?? undefined,
		initialPrice: pwyw ? 0 : Number(ticketForm.initialPrice),
		name: ticketForm.name ?? '',
		tickets_pool_id: poolId,
		bundleSize: ticketForm.bundleSize ? Number(ticketForm.bundleSize) : undefined,
		secondaryRoyalty: Number(ticketForm.secondaryRoyalty),
		sellingTime: convertDateWithTimezoneToUTC(_sellingTime, timezone),
		offSellingTime: _offSellingTime,
		image: ticketForm.image ?? '',
		displayType: ticketForm.displayType ?? '',
		template: {
			image: ticketForm.image,
			displayType: ticketForm.displayType,
		},
		payWhatYouWant: pwyw,
		minPrice: pwyw ? Number(ticketForm.minPrice) : 0,
		maxPrice: pwyw ? Number(ticketForm.maxPrice) : 0,
		max_quantity: ticketForm.maxQuantity ? Number(ticketForm.maxQuantity) : null,
		percentageFee: ticketForm.percentageFee !== null ? Number(ticketForm.percentageFee) : null,
		fixedFee: ticketForm.fixedFee !== null ? Number(ticketForm.fixedFee) : null,
		// Persisted as TicketType.Color (json `color`). Omit when unset so an edit doesn't blank it.
		color: ticketForm.color || undefined,
	};

	return _ticketForm;
};

/** Maps the deferred ticket image to persisted `image` + `displayType` at Save time. Contract: TicketDetails/CLAUDE.md 'Ticket image — deferred staging'. */
export const resolveTicketImage = async (
	values: CreateTicketForm,
	mutateUploadImage: (args: UploadImageProps) => Promise<{ data: { image_url: string } }>,
	eventId: number
): Promise<{ image: string; displayType: string }> => {
	const staged = values.ticketImageStaged?.[0];

	if (!staged) {
		return { image: '', displayType: '' };
	}

	if (isNewImg(staged)) {
		const response = await mutateUploadImage({
			image: { url: staged.url, base64: staged.base64 as string, name: staged.name },
			prefix: `${eventId}-${values.name || 'ticket'}`,
			index: 0,
			type: 'template',
		});
		return { image: response.data.image_url, displayType: 'image' };
	}

	// Persisted image left untouched — keep what fillForm loaded.
	return { image: values.image ?? '', displayType: values.displayType || 'image' };
};

export const getBooleanString = (boolean: boolean): BooleanString => {
	const booleanString: BooleanString = boolean ? 'true' : 'false';
	return booleanString;
};

export const fillForm = (ticket: TicketBody | undefined, timezone: string): CreateTicketForm | undefined => {
	const ticketCamel = camelizeKeys(ticket, {
		// This function keeps ID as ID
		process: keepUpperCase,
	});
	if (ticketCamel) {
		const _form = {
			...ticketCamel,
			// 0 = "No pool" so the explicit default option shows as selected for un-pooled tiers.
			poolId: typeof ticketCamel.ticketsPoolId === 'number' ? ticketCamel.ticketsPoolId : 0,
			buyerAbsorbPaymentFee: getBooleanString(ticketCamel.buyerAbsorbPaymentFee),
			buyerAbsorbTixologistFee: getBooleanString(ticketCamel.buyerAbsorbTixologistFee),
			sellingTime: convertUTCToDate(ticketCamel.sellingTime, timezone),
			offSellingTime: ticketCamel.offSellingTime ? convertUTCToDate(ticketCamel.offSellingTime, timezone) : null,
			template: ticketCamel.image,
			ticketImage: ticketCamel.image,
			// Seed the deferred staging field from the persisted image URL (no base64 ⇒
			// isNewImg() is false ⇒ it is not re-uploaded unless the user replaces it).
			ticketImageStaged: ticketCamel.image ? [{ url: ticketCamel.image, name: 'ticket-image' }] : [],
			displayType: ticketCamel.template.displayType ?? '',
			ticketVisibility: ticketCamel.ticketVisibility,
			privateLink: !!ticketCamel.hiddenCode,
			hiddenCode: ticketCamel.hiddenCode,
			showImage: ticketCamel.showImage ?? false,
			seatsCategoryKey: ticketCamel.seatsCategoryKey ? String(ticketCamel.seatsCategoryKey) : undefined,
			presaleCode: ticketCamel.presaleCode || '',
			alwaysShowTickets: ticketCamel.alwaysShowTickets ?? false,
			payWhatYouWant: ticketCamel.payWhatYouWant,
			minPrice: ticketCamel.minPrice,
			maxPrice: ticketCamel.maxPrice,
			// "No limit" / "no bundle" is the empty field. Normalize a legacy 0 (and null/undefined) to
			// empty so the clearable count steppers (min 1) read as empty, not a literal 0. createBody
			// already maps a falsy value back to null/undefined on save, so the round-trip is stable.
			maxQuantity: ticketCamel.maxQuantity || null,
			bundleSize: ticketCamel.bundleSize || undefined,
			percentageFee: ticketCamel.percentageFee ?? null,
			fixedFee: ticketCamel.fixedFee ?? null,
			color: ticketCamel.color ?? null,
		};
		return _form;
	}
};

export const getTicketTypeCanGoLive = (eventCanGoLive: EventCanGoLive, ticketTypeId?: number): EventCanGoLive => {
	const ticketsDetails = ticketTypeId
		? { [ticketTypeId]: eventCanGoLive.ticketTypeDetails[ticketTypeId] }
		: { 0: true };

	return {
		hasTickets: true,
		eventDetails: true,
		allTicketDetails: true,
		ticketTypeDetails: ticketsDetails,
		partnerInfo: eventCanGoLive.partnerInfo,
	};
};

export const showReqFieldsModal = (event: EventData, eventCanGoLive: EventCanGoLive, ticketPrice: number): boolean => {
	const isFreeEvent = event.ticketTypes.every((ticket) => ticket.initialPrice === 0);
	const eventIsLive = event.liveOnWidget || event.liveOnWebpage;
	const missingInfo = !eventCanGoLive.partnerInfo.taxInfo || !eventCanGoLive.partnerInfo.payoutsInfo;
	if (eventIsLive && isFreeEvent && ticketPrice > 0 && missingInfo) {
		return true;
	}

	return false;
};
